/**
 * useTurnNotifications — fires a desktop / mobile Notification when
 * the current player rotates to ME, the tab is hidden, the user has
 * opted in, and the browser has granted permission.
 *
 * Closes KI-4 ("No push notification when it's your turn in an
 * online game").
 *
 * Scope guards (each one suppresses the notification — fail-safe):
 *   • `Notification` / `document.hidden` not present (old / non-PWA
 *     browsers, in-app webviews) → no-op.
 *   • `localStorage["mm.turn_notify"] !== "true"` (user hasn't opted
 *     in via the Profile-page toggle) → no-op.
 *   • `Notification.permission !== "granted"` → no-op.
 *   • Tab is currently focused (`!document.hidden`) → no-op
 *     (no point notifying when the user is staring at the page).
 *   • Game isn't online (pass-and-play / vs-AI / Daily) → no-op.
 *     Single-device modes don't benefit; the player is right there.
 *   • `current_player_index` didn't actually change since last
 *     render → no-op (no fresh transition to announce).
 *
 * The hook runs on every render but only fires Notification when the
 * transition condition holds, so it's cheap.
 */
import { useEffect, useRef } from "react";


export const TURN_NOTIFY_KEY = "mm.turn_notify";


export function isTurnNotifyEnabled() {
  try {
    return window.localStorage.getItem(TURN_NOTIFY_KEY) === "true";
  } catch {
    return false;
  }
}


export function setTurnNotifyEnabled(enabled) {
  try {
    window.localStorage.setItem(TURN_NOTIFY_KEY, enabled ? "true" : "false");
  } catch {
    // localStorage disabled — preference just won't persist; the
    // current session keeps working off in-memory state.
  }
}


/**
 * Helper used by the Profile-page toggle. Prompts for browser
 * permission AND stores the localStorage preference. Returns the
 * final permission state so the caller can show a friendly toast
 * (e.g. "blocked — adjust browser settings").
 */
export async function requestTurnNotifyPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") {
    setTurnNotifyEnabled(true);
    return "granted";
  }
  if (Notification.permission === "denied") {
    // The browser refuses to re-prompt after a hard deny — user has
    // to flip the toggle in their browser's site settings. Reflect
    // that honestly instead of silently no-op'ing.
    setTurnNotifyEnabled(false);
    return "denied";
  }
  try {
    const result = await Notification.requestPermission();
    setTurnNotifyEnabled(result === "granted");
    return result;
  } catch {
    setTurnNotifyEnabled(false);
    return "denied";
  }
}


export function useTurnNotifications({ game, currentUserId, isOnline }) {
  const prevIdxRef = useRef(null);

  useEffect(() => {
    if (!game || !isOnline) {
      // Reset the tracker so a future game starts clean.
      prevIdxRef.current = game?.current_player_index ?? null;
      return;
    }
    if (game.status !== "active") return;

    const cur = game.current_player_index;
    const prev = prevIdxRef.current;
    prevIdxRef.current = cur;

    // No transition → nothing to notify about.
    if (prev === null || prev === cur) return;

    // Browser support
    if (typeof Notification === "undefined") return;
    if (typeof document === "undefined") return;

    // Don't notify when the user is already looking at the page.
    if (!document.hidden) return;

    // User opt-in + browser permission.
    if (!isTurnNotifyEnabled()) return;
    if (Notification.permission !== "granted") return;

    // Compute "my" slot from user_id_map. Tolerate absence (single-
    // player modes) — the isOnline guard above already filters most.
    const map = game.user_id_map || {};
    const myIdx = Object.entries(map).find(([_, uid]) => uid === currentUserId)?.[0];
    if (myIdx === undefined) return;
    if (Number(myIdx) !== cur) return;

    try {
      const n = new Notification("Your turn — Market Melee", {
        body: "Tap to come back and play.",
        tag: "market-melee-turn",  // collapses repeats so the OS shows one
        renotify: true,
      });
      // Clicking the notification refocuses the tab.
      n.onclick = () => {
        try {
          window.focus();
          n.close();
        } catch {/* ignored */}
      };
    } catch {
      // Some browsers throw if the page hasn't been interacted with
      // yet — fail-soft.
    }
  }, [game, currentUserId, isOnline]);
}
